import UploadFile from 'components/form/UploadFile';
import HelpIcon from 'components/icons/HelpIcon';
import { HelpBox } from 'components/v2/help-boxes';
import { EPD_DEVELOPMENT_VERIFICATION_TEMPLATES_URL } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import helpIcon from 'images/icons/svg/info.svg';
import React, { ChangeEvent, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionState, EPDModel, VerificationType } from 'services/EpdClient';
import styled from 'styled-components';
import { A, InfoBox } from 'styles/Styles.styled';
import { dateToIsoString } from 'util/utils';
import { Validation } from './Validation';
import { FixedLabel, FixedLabelTop } from './styles';
import { WizardStepValidationSchema } from './types';

type FileUploaderType = 'verificationReport' | 'processCertificate';

const WizardStep3: React.FunctionComponent<{
  epd?: EPDModel;
  onChangeEpd(propertyName: string, value: any): void;
  onUploadFile(field: string, file: File, documentTypeId: string | null, documentLanguageId: string | null): void;
  onRemoveFile(fileId: string): void;
  setUpdatedEpd?: (updatedEpd: EPDModel) => void;
  goToStep?: (step: number) => void;
  validation: WizardStepValidationSchema;
}> = ({ epd, onChangeEpd, onUploadFile, onRemoveFile, validation, goToStep }) => {
  const { t } = useTranslation();
  const { company } = useContext(CompanyContext);
  const [fileUploaders, setFileUploaders] = useState<FileUploaderType[]>([]);

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>, propertyName: string, fileUploader: FileUploaderType) => {
    const target = e.target as HTMLInputElement;
    let file = target && target.files && target.files[0];

    if (!file || !epd) {
      return null;
    }

    try {
      setFileUploaders((old) => [...old, fileUploader]);
      await onUploadFile(propertyName, file, null, null);
    } finally {
      setFileUploaders((old) => {
        const index = old.indexOf(fileUploader);
        if (index === -1) {
          throw new Error('Uploader was removed more than once.');
        }
        return old.filter((_, i) => i !== index);
      });
    }
  };

  if (epd?.verificationType === VerificationType.EPDPreverifiedTool && company?.hasToolVerificationType) {
    return (
      <div>
        <HelpBox>This verification flow is based on the pre-verified tool, no actions needed</HelpBox>
      </div>
    );
  }

  return (
    <div>
      <FixedRow style={{ gridTemplateRows: '50px' }} state={epd?.epdVerifierState?.actionState}>
        <FixedLabelTop>{t('epdWizard.wizardStep3.properties.verifier')}</FixedLabelTop>
        {epd?.epdVerifier?.displayName ?? (
          <>
            <div style={{ minWidth: '40%' }}>{t('epdWizard.wizardStep3.properties.declaration')}</div>
            <div style={{ margin: '1rem 0 1rem 0', minWidth: '30%' }}>
              <StepLink
                style={{ color: 'inherit', textDecoration: 'none' }}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  goToStep?.(0);
                }}
              >
                {t('epdWizard.wizardStep3.properties.getStarted')}
              </StepLink>
            </div>
          </>
        )}
      </FixedRow>

      <FixedRow state={epd?.verificationReportsState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep3.properties.verificationReport')}
          <Validation validation={validation} field="verificationReports" epd={epd} />
        </FixedLabelTop>
        <div>
          {epd?.verificationTemplateUrlState?.actionState !== ActionState.Hidden && (
            <InfoBox style={{ alignItems: 'center' }}>
              <img src={helpIcon} alt="information" style={{ height: '35px', marginRight: '1rem' }} />
              <div>
                Please use the latest{' '}
                <A href={EPD_DEVELOPMENT_VERIFICATION_TEMPLATES_URL} target="_blank" rel="noopener noreferrer">
                  verification report template
                </A>{' '}
                to record your results.
              </div>
            </InfoBox>
          )}
          <UploadFile
            accept=".docx,.pdf,.xlsx"
            validExtensions={['docx', 'pdf', 'xlsx']}
            title={
              'Please upload the verification report in either DOCX, PDF, or XLSX format using the template above. Multiple reports can be uploaded if necessary.'
            }
            uploadFile={(e, field) => uploadFile(e, field, 'verificationReport')}
            disabled={epd?.verificationReportsState?.actionState === ActionState.Disabled}
            documents={epd?.verificationReports}
            onRemoveFile={onRemoveFile}
            field="VerificationReport"
            showUploadingIndicator={fileUploaders.includes('verificationReport')}
          />
        </div>
      </FixedRow>

      <FixedRow state={epd?.processCertificatesState?.actionState}>
        <FixedLabelTop>
          {t('epdWizard.wizardStep3.properties.processCertificates')}
          <Validation validation={validation} field="processCertificates" epd={epd} />
        </FixedLabelTop>
        <div style={{ width: '100%' }}>
          <UploadFile
            accept=".pdf"
            validExtensions={['pdf']}
            title={t('epdWizard.wizardStep3.upload.processCertificates')}
            uploadFile={(e, field) => uploadFile(e, field, 'processCertificate')}
            disabled={epd?.processCertificatesState?.actionState === ActionState.Disabled}
            uploadDisabled={(epd?.processCertificates?.length ?? 0) >= 1}
            documents={epd?.processCertificates}
            onRemoveFile={onRemoveFile}
            field="processCertificate"
            showUploadingIndicator={fileUploaders.includes('processCertificate')}
          />
        </div>
      </FixedRow>
      <FixedRow state={epd?.approvalTimestampState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep3.properties.approvalTimestamp.title')}
          <HelpIcon content={t('epdWizard.wizardStep3.properties.approvalTimestamp.tooltip')} />
        </FixedLabel>
        <em>{dateToIsoString(epd?.approvalTimestamp)}</em>
      </FixedRow>
    </div>
  );
};

const StepLink = styled.a`
  padding: 0.5rem 1rem;
  border: solid 1px ${(props) => props.theme.colors.gray};
  font-family: ${(props) => props.theme.fontFamilies.heading};
  font-size: 1.1rem;
  font-style: normal;
  text-decoration: none;
  color: text;

  :hover {
    background: ${(props) => props.theme.colors.lightGray};
  }

  ${(props) => props.theme.media.tablet} {
    margin: 0.5rem;
  }
`;

export const FixedRow = styled.div<{ state?: ActionState }>`
  display: ${(props) => (props?.state === ActionState.Hidden ? 'none' : 'flex')};
  padding: 0.5rem 0;
  margin-top: 2rem;
  flex-wrap: wrap;
  align-items: center;

  ${(props) => props.theme.media.tablet} {
    flex-wrap: nowrap;
    margin-top: 0;
  }
`;

export default WizardStep3;
